// ─── Client observability — BOTH backends + OpenObserve replay ──────────────
//
// Two cooperating sinks, gated together by the server's runtime-config:
//
//   1. OpenObserve RUM + logs + rrweb session replay (THIS file). rrweb is a
//      proprietary RUM payload (not OTLP), so it posts straight to OpenObserve
//      via the `/o2` sub-path — it can only ever live in OpenObserve.
//   2. OpenTelemetry Web traces (./otel-web) → the shared OTel Collector via
//      `/otlp`, which fans them out to SigNoz AND OpenObserve. This is what
//      gets client errors, user actions and end-to-end (client→server) traces
//      into BOTH tools, not just OpenObserve.
//
// `track()` writes to both worlds at once: an OTLP span (both backends) AND a
// RUM action (OpenObserve replay timeline), so a click is queryable in SigNoz
// and visible on the replay scrubber. Everything is OFF unless the server says
// so, and every path swallows its own errors — telemetry must NEVER break boot.

import { openobserveRum } from '@openobserve/browser-rum';
import { openobserveLogs } from '@openobserve/browser-logs';
import type { Attributes } from '@opentelemetry/api';
import { useAuthStore } from './authStore';

/** Shape of `GET /api/runtime-config` (see CANONICAL DECISIONS / apps/api). */
interface RuntimeConfig {
  env: string;
  rum: {
    enabled: boolean;
    /** Same-origin sub-path the obs edge serves OpenObserve on (default `/o2`). */
    collectorBase: string;
    org: string;
    token: string;
    stream: string;
    service: string;
  };
  /** Browser → shared OTel Collector (→ SigNoz + OpenObserve). */
  tracing?: {
    enabled: boolean;
    /** Same-origin sub-path Caddy proxies to the collector's OTLP/HTTP port. */
    endpoint: string;
  };
}

/** Set once a successful SDK init has happened, so the error boundary can report. */
let logsReady = false;
/** Set once OTLP web tracing is live, so track()/reportError reach both backends. */
let tracingReady = false;

/**
 * Map our same-origin `collectorBase` (e.g. `/o2`) onto the OpenObserve SDK's
 * `site` (a host, optionally with a base path) + `insecureHTTP` (scheme) fields.
 * The SDK builds its intake as `{http|https}://{site}/{apiVersion}/...`, so for a
 * reverse-proxied sub-path we hand it `"<host><base>"` and let the current page's
 * protocol decide the scheme. Result: beacons hit `<origin>/o2/api/...` — exactly
 * the gated, same-origin ingest path, no CORS.
 */
function siteFromCollectorBase(collectorBase: string): { site: string; insecureHTTP: boolean } {
  const base = (collectorBase || '/o2').replace(/\/+$/, '');
  const insecureHTTP = window.location.protocol !== 'https:';
  return { site: `${window.location.host}${base}`, insecureHTTP };
}

/** The signed-in user, for per-span/RUM attribution (null when logged out). */
function currentIdentity(): { id?: string; email?: string } | null {
  try {
    const u = useAuthStore.getState().user;
    return u ? { id: u.id, email: u.email ?? undefined } : null;
  } catch {
    return null;
  }
}

/** OpenObserve RUM session id, to cross-link OTLP spans to the rrweb replay. */
function rumSessionId(): string | undefined {
  try {
    return (openobserveRum.getInternalContext?.() as { session_id?: string } | undefined)?.session_id;
  } catch {
    return undefined;
  }
}

/**
 * Fire-and-forget client telemetry init. Safe to call before render; resolves to
 * void and never throws. A complete no-op when telemetry is disabled or
 * unreachable.
 */
export async function initObservability(): Promise<void> {
  try {
    const res = await fetch('/api/runtime-config', {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return;

    const cfg = (await res.json()) as RuntimeConfig;
    const version = import.meta.env.VITE_BUILD_VERSION ?? undefined;

    // ── 1. OpenObserve RUM + logs + rrweb session replay (OpenObserve only) ──
    const rum = cfg?.rum;
    if (rum && rum.enabled && rum.token && rum.org) {
      const { site, insecureHTTP } = siteFromCollectorBase(rum.collectorBase);

      openobserveRum.init({
        applicationId: rum.service,
        clientToken: rum.token,
        site,
        organizationIdentifier: rum.org,
        service: rum.service,
        env: cfg.env,
        version,
        insecureHTTP,
        apiVersion: 'v1',
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        defaultPrivacyLevel: 'mask-user-input',
      });

      openobserveLogs.init({
        clientToken: rum.token,
        site,
        organizationIdentifier: rum.org,
        service: rum.service,
        env: cfg.env,
        version,
        insecureHTTP,
        apiVersion: 'v1',
        forwardErrorsToLogs: true,
      });

      openobserveRum.startSessionReplayRecording();
      logsReady = true;

      // Attribute the RUM session (and replay) to the signed-in user, live.
      bindRumUser();
    }

    // ── 2. OpenTelemetry Web traces → collector → SigNoz + OpenObserve ──
    const tracing = cfg?.tracing;
    if (tracing?.enabled) {
      const { initWebTracing } = await import('./otel-web');
      initWebTracing({
        env: cfg.env,
        service: rum?.service || 'pcp-web',
        version,
        endpoint: tracing.endpoint || '/otlp',
        getIdentity: currentIdentity,
        getSessionId: rumSessionId,
      });
      tracingReady = true;

      installInteractionCapture();
      void reportWebVitals();
    }
  } catch {
    // Telemetry must never break app startup — swallow everything.
  }
}

/** Keep the OpenObserve RUM user in sync with auth state (initial + on change). */
function bindRumUser(): void {
  const apply = (u: ReturnType<typeof currentIdentity>): void => {
    try {
      if (u?.id) openobserveRum.setUser({ id: u.id, email: u.email });
    } catch {
      /* ignore */
    }
  };
  apply(currentIdentity());
  try {
    useAuthStore.subscribe((s) => apply(s.user ? { id: s.user.id, email: s.user.email ?? undefined } : null));
  } catch {
    /* ignore */
  }
}

/**
 * Record a semantic user action. Writes to BOTH worlds: an OTLP span (→ SigNoz
 * + OpenObserve, queryable) and a RUM action (→ OpenObserve replay timeline). A
 * cheap no-op until telemetry is initialised, so always safe to call.
 */
export function track(name: string, attributes?: Attributes): void {
  if (tracingReady) {
    // Lazily reach the OTLP emitter (module is already loaded once tracingReady).
    void import('./otel-web').then((m) => m.emitSpanEvent(name, attributes)).catch(() => {});
  }
  try {
    if (logsReady) openobserveRum.addAction(name, attributes as Record<string, unknown> | undefined);
  } catch {
    /* ignore */
  }
}

/**
 * Report a caught React render error. Goes to OpenObserve (logs) AND, via OTLP,
 * to SigNoz + OpenObserve as an error span. No-op until telemetry is initialised.
 */
export function reportError(error: Error, context?: Record<string, unknown>): void {
  if (logsReady) {
    try {
      openobserveLogs.logger.error(error.message, { error, ...context });
    } catch {
      /* ignore */
    }
  }
  if (tracingReady) {
    void import('./otel-web')
      .then((m) => m.recordClientError(error, context as Attributes | undefined))
      .catch(() => {});
  }
}

/**
 * Capture-phase click listener → one `click` event per interactive element, with
 * its visible label (never input VALUES — privacy). Gives both backends a
 * semantic stream of "what the user clicked", on top of the auto fetch spans.
 */
function installInteractionCapture(): void {
  if (typeof document === 'undefined') return;
  document.addEventListener(
    'click',
    (e) => {
      try {
        const el = (e.target as HTMLElement | null)?.closest(
          'button, a, [role="button"], [role="tab"], [role="menuitem"], [data-track]',
        );
        if (!el) return;
        const label =
          el.getAttribute('data-track') ||
          el.getAttribute('aria-label') ||
          el.textContent?.trim().slice(0, 80) ||
          el.tagName.toLowerCase();
        track('click', { 'ui.label': label, 'ui.tag': el.tagName.toLowerCase() });
      } catch {
        /* never let interception throw */
      }
    },
    { capture: true, passive: true },
  );
}

/** Report Core Web Vitals (CLS/LCP/INP/FCP/TTFB) as events into both backends. */
async function reportWebVitals(): Promise<void> {
  try {
    const { onCLS, onLCP, onINP, onFCP, onTTFB } = await import('web-vitals');
    const report = (m: { name: string; value: number; rating: string; id: string }): void =>
      track('web_vital', {
        'vital.name': m.name,
        'vital.value': m.value,
        'vital.rating': m.rating,
        'vital.id': m.id,
      });
    onCLS(report);
    onLCP(report);
    onINP(report);
    onFCP(report);
    onTTFB(report);
  } catch {
    /* web-vitals optional — ignore */
  }
}
